/**
 * TennisHero — 鼠标力场推开 + 点击发射网球 v6
 * 效果：整齐网格排列，鼠标力场推开单词，点击发射网球
 */
import { useEffect, useRef, useCallback } from 'react';

const KEYWORD_GROUPS = [
  'real-time pose', '120fps tracking', 'kinematic chain', 'serve biomechanics',
  'YOLOv8 detection', '98.7% accuracy', 'optical flow', 'ball trajectory',
  'graph neural net', 'joint angle', '3D court mapping', 'contact-point',
  'string deformation', 'temporal CNN', 'stroke segmentation', 'wrist snap',
  '2100 RPM topspin', 'COCO skeleton', 'kinetic energy', 'racket-ball impact',
  'serve 220km/h', 'lower limb', 'ground reaction', 'shoulder rotation',
  'reaction time', 'EMG validated', 'footwork efficiency', 'court coverage',
  'aerobic threshold', 'injury risk', 'joint stress', 'hip-shoulder分离',
  'power generation', 'reinforcement learning', 'rally strategy', 'video understanding',
  'shot classification', 'attention mechanism', 'focus heatmap', 'transformer',
  'sequence prediction', 'self-supervised', 'contrastive learning', 'LLM coaching',
  'federated learning', 'diffusion model', 'synthetic data', 'personalized drills',
  'edge inference', '30ms on-court', 'cloud analytics', 'match dashboard',
  '4K video pipeline', 'AWS SageMaker', 'IBM SlamTracker', 'Hawk-Eye precision',
  'AI director', 'multi-modal fusion', 'adaptive training', 'LAFIT AI Coach',
  'swing analysis', 'pose estimation', 'ball tracking', 'serve analysis',
]

interface Ball {
  x: number; y: number; vx: number; vy: number
  angle: number; age: number
  trail: { x: number; y: number; age: number }[]
}

interface Word {
  id: number
  text: string
  x: number; y: number
  tx: number; ty: number
  vx: number; vy: number
  w: number; h: number
  color: string
  fontSize: number
  alpha: number
}

export default function TennisHero() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const ballsRef = useRef<Ball[]>([])
  const stateRef = useRef({ words: [] as Word[] })
  const mouseRef = useRef({ x: -9999, y: -9999 })

  // 暴露到 window 用于防止 tree-shaking
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).__lafit_balls_ref__ = ballsRef

  // ── 初始化规整网格 ─────────────────────────────────────────────────
  const initWords = useCallback((W: number, H: number) => {
    const s = stateRef.current
    if (s.words.length > 0) return

    const tempCanvas = document.createElement('canvas')
    tempCanvas.width = W; tempCanvas.height = H
    const ctx = tempCanvas.getContext('2d')!

    const FOCAL_X = W / 2
    const FOCAL_Y = H * 0.38
    const MAX_DIST = Math.sqrt(W * W + H * H) * 0.75

    const COLOR_FULL = '#40e8ff'
    const COLOR_MID  = '#00c8ee'
    const COLOR_DIM   = '#0088aa'

    const FONT_SIZE_BASE = 13
    const CELL_H = 28
    const CELL_W_MIN = 130
    const COLS = Math.max(4, Math.floor(W / CELL_W_MIN))
    const CELL_W = W / COLS

    const wordObjs: Word[] = []
    let id = 0

    for (let col = 0; col < COLS; col++) {
      const colCenterX = col * CELL_W + CELL_W / 2
      for (let row = 0; row < 200; row++) {
        const kwIdx = (col * 11 + row * 3) % KEYWORD_GROUPS.length
        const text = KEYWORD_GROUPS[kwIdx]

        const dx = colCenterX - FOCAL_X
        const dy = row * CELL_H + 10 - FOCAL_Y
        const dist = Math.sqrt(dx * dx + dy * dy) / MAX_DIST
        const depthFactor = Math.max(0.48, Math.pow(Math.max(0, 1 - dist), 0.7))
        const fontSize = Math.round(FONT_SIZE_BASE * depthFactor + 6)
        ctx.font = `${fontSize}px 'Courier New', monospace`
        const tw = ctx.measureText(text).width
        const th = fontSize * 1.25

        const cx = col * CELL_W + (CELL_W - tw) / 2
        const cy = row * CELL_H

        const radialAlpha = Math.pow(Math.max(0, 1 - dist), 1.4)
        let alpha = radialAlpha
        alpha = Math.max(0.15, Math.min(1, alpha))

        const color = alpha > 0.70 ? COLOR_FULL
          : alpha > 0.38 ? COLOR_MID
          : COLOR_DIM

        wordObjs.push({
          id: id++, text, x: cx, y: cy,
          tx: cx, ty: cy,
          vx: 0, vy: 0,
          w: tw, h: th,
          color, fontSize, alpha,
        })

        if (Math.random() > 0.82) {
          const kw2 = (col * 11 + row * 3 + 5) % KEYWORD_GROUPS.length
          const text2 = KEYWORD_GROUPS[kw2]
          const fs2 = Math.round(fontSize * 0.82 + 4)
          ctx.font = `${fs2}px 'Courier New', monospace`
          const tw2 = ctx.measureText(text2).width
          const cx2 = col * CELL_W + (CELL_W - tw2) / 2
          const cy2 = (row + 1) * CELL_H + 2
          const alpha2 = Math.max(0.12, alpha * 0.85)
          const color2 = alpha2 > 0.70 ? COLOR_FULL
            : alpha2 > 0.38 ? COLOR_MID
            : COLOR_DIM
          wordObjs.push({
            id: id++, text: text2, x: cx2, y: cy2,
            tx: cx2, ty: cy2,
            vx: 0, vy: 0,
            w: tw2, h: fs2 * 1.25,
            color: color2, fontSize: fs2, alpha: alpha2,
          })
          row++
        }

        if ((row + 1) * CELL_H > H + 100) break
      }
    }

    s.words = wordObjs
  }, [])

  // ── 发射网球（穿过点击点） ──────────────────────────────────────────
  const shootBall = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    const clickX = (clientX - rect.left) * scaleX
    const clickY = (clientY - rect.top) * scaleY

    if (ballsRef.current.length >= 3) return

    const H = canvas.height
    const GRAVITY = 0.28
    const T_apex = Math.max(20, (clickX + 25) / 18)
    const vx = (clickX + 25) / T_apex
    const vy = (clickY - H - 60 - 0.14 * T_apex * T_apex) / T_apex

    ballsRef.current.push({
      x: -25, y: H + 60, vx, vy,
      angle: 0, age: 0, trail: [],
    })
  }, [])

  // ── 主循环 ──────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!

    let rafId = 0

    function resize() {
      const parent = canvas.parentElement
      if (!parent) return
      const { clientWidth: W, clientHeight: H } = parent
      canvas.width = W; canvas.height = H
      stateRef.current.words = []
      initWords(W, H)
    }

    function tick() {
      const now = performance.now()
      const s = stateRef.current
      const W = canvas.width; const H = canvas.height
      if (W === 0 || H === 0) { rafId = requestAnimationFrame(tick); return }

      const mx = mouseRef.current.x
      const my = mouseRef.current.y

      // ── 更新球 ─────────────────────────────────────────────────
      ballsRef.current = ballsRef.current.filter(ball => {
        ball.x += ball.vx
        ball.y += ball.vy
        ball.vy += 0.28
        ball.angle += 0.2
        ball.age++
        ball.trail.push({ x: ball.x, y: ball.y, age: 0 })
        if (ball.trail.length > 28) ball.trail.shift()
        ball.trail.forEach(t => t.age++)
        if (ball.x > W + 80 || ball.y > H + 80 || ball.y < -80) return false
        return true
      })

      // ── 单词物理 ───────────────────────────────────────────────
      s.words.forEach(w => {
        // 弹簧回归（极弱，让推开效果明显）
        w.vx += (w.tx - w.x) * 0.025
        w.vy += (w.ty - w.y) * 0.025

        // 鼠标力场（推开）：用词中心到鼠标方向，永远是排斥力
        if (mx > -9000) {
          const cx = w.x + w.w * 0.5
          const cy = w.y + w.h * 0.5
          const bdx = cx - mx   // 从鼠标指向词中心（词被推向反方向）
          const bdy = cy - my
          const bd = Math.sqrt(bdx * bdx + bdy * bdy)
          const FIELD_RADIUS = 150  // 力场半径，缩小一半

          if (bd < FIELD_RADIUS && bd > 1) {
            const strength = (1 - bd / FIELD_RADIUS) * 18  // 最靠近处每帧 18px/s
            w.vx += (bdx / bd) * strength
            w.vy += (bdy / bd) * strength
          }
        }

        // 球力场（推开）：用词中心到球方向，词被球推离
        ballsRef.current.forEach(ball => {
          const cx = w.x + w.w * 0.5
          const cy = w.y + w.h * 0.5
          const bdx = cx - ball.x
          const bdy = cy - ball.y
          const bd = Math.sqrt(bdx * bdx + bdy * bdy)
          const FIELD_RADIUS = 280
          if (bd < FIELD_RADIUS && bd > 1) {
            const strength = (1 - bd / FIELD_RADIUS) * 14
            w.vx += (bdx / bd) * strength
            w.vy += (bdy / bd) * strength
          }
        })

        // 边界
        if (w.x < 4) w.vx += (4 - w.x) * 0.3
        if (w.x + w.w > W - 4) w.vx += (W - 4 - w.x - w.w) * 0.3
        if (w.y < 4) w.vy += (4 - w.y) * 0.3
        if (w.y > H - 4) w.vy += (H - 4 - w.y) * 0.3

        // 阻尼
        w.vx *= 0.82; w.vy *= 0.82
        w.x += w.vx; w.y += w.vy
      })

      // ── 渲染背景 ──────────────────────────────────────────────
      ctx.fillStyle = '#050c18'
      ctx.fillRect(0, 0, W, H)

      ctx.strokeStyle = 'rgba(30,80,160,0.06)'
      ctx.lineWidth = 1
      const GRID = 60
      for (let x = 0; x < W; x += GRID) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke()
      }
      for (let y = 0; y < H; y += GRID) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke()
      }

      // ── 渲染单词 ──────────────────────────────────────────────
      ctx.save()
      ctx.textBaseline = 'top'
      s.words.forEach(w => {
        ctx.globalAlpha = w.alpha
        ctx.shadowColor = w.color
        ctx.shadowBlur = w.alpha > 0.75 ? 8 : 3
        ctx.fillStyle = w.color
        ctx.font = `${w.fontSize}px 'Courier New', monospace`
        ctx.fillText(w.text, w.x, w.y)
        ctx.shadowBlur = 0
        ctx.fillText(w.text, w.x, w.y)
      })
      ctx.globalAlpha = 1
      ctx.restore()

      // ── 渲染球 ────────────────────────────────────────────────
      ballsRef.current.forEach(ball => {
        ball.trail.forEach((t, i) => {
          const alpha = (1 - t.age / 30) * 0.35
          if (alpha <= 0) return
          const r = 15 + (i / ball.trail.length) * 50
          const grad = ctx.createRadialGradient(t.x, t.y, 0, t.x, t.y, r)
          grad.addColorStop(0, `rgba(0,220,255,${alpha})`)
          grad.addColorStop(1, 'rgba(0,100,255,0)')
          ctx.beginPath()
          ctx.arc(t.x, t.y, r, 0, Math.PI * 2)
          ctx.fillStyle = grad
          ctx.fill()
        })
        drawBall(ctx, ball.x, ball.y, ball.angle)
      })

      // ── 鼠标力场指示 ──────────────────────────────────────────
      if (mx > 0 && mx < W && my > 0 && my < H) {
        ctx.save()
        const fieldGrad = ctx.createRadialGradient(mx, my, 0, mx, my, 300)
        fieldGrad.addColorStop(0, 'rgba(0,220,255,0.10)')
        fieldGrad.addColorStop(0.6, 'rgba(0,150,255,0.04)')
        fieldGrad.addColorStop(1, 'rgba(0,80,255,0)')
        ctx.beginPath()
        ctx.arc(mx, my, 300, 0, Math.PI * 2)
        ctx.fillStyle = fieldGrad
        ctx.fill()

        ctx.strokeStyle = 'rgba(0,220,255,0.20)'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 6])
        ctx.beginPath(); ctx.moveTo(mx - 18, my); ctx.lineTo(mx + 18, my); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(mx, my - 18); ctx.lineTo(mx, my + 18); ctx.stroke()
        ctx.setLineDash([])
        ctx.restore()
      }

      // ── 提示 ─────────────────────────────────────────────────
      if (ballsRef.current.length === 0) {
        ctx.save()
        ctx.globalAlpha = 0.3 + 0.2 * Math.sin(now * 0.0025)
        ctx.fillStyle = '#40c8ff'
        ctx.font = '13px Arial'
        ctx.textAlign = 'center'
        ctx.fillText('移动鼠标推开文字  ·  点击发射网球', W / 2, H - 36)
        ctx.restore()
      }

      rafId = requestAnimationFrame(tick)
    }

    resize()
    window.addEventListener('resize', resize)
    rafId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', resize)
    }
  }, [initWords])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    mouseRef.current = {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    mouseRef.current = { x: -9999, y: -9999 }
  }, [])

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    shootBall(e.clientX, e.clientY)
  }, [shootBall])

  const handleTouch = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const touch = e.touches[0]
    mouseRef.current = {
      x: (touch.clientX - rect.left) * (canvas.width / rect.width),
      y: (touch.clientY - rect.top) * (canvas.height / rect.height),
    }
    shootBall(touch.clientX, touch.clientY)
  }, [shootBall])

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchMove={handleTouch}
        onTouchStart={handleTouch}
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          cursor: 'crosshair', display: 'block',
        }}
      />
    </div>
  )
}

// ── 绘制网球 ───────────────────────────────────────────────────────────
function drawBall(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number) {
  const R = 13
  const grad = ctx.createRadialGradient(x, y, R * 0.5, x, y, R * 3.5)
  grad.addColorStop(0, 'rgba(0,220,255,0.6)')
  grad.addColorStop(0.4, 'rgba(0,150,255,0.25)')
  grad.addColorStop(1, 'rgba(0,80,255,0)')
  ctx.beginPath()
  ctx.arc(x, y, R * 3.5, 0, Math.PI * 2)
  ctx.fillStyle = grad
  ctx.fill()

  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.shadowColor = '#00dcff'
  ctx.shadowBlur = 20
  ctx.beginPath()
  ctx.arc(0, 0, R, 0, Math.PI * 2)
  ctx.fillStyle = '#00d4ff'
  ctx.fill()
  ctx.shadowBlur = 0
  ctx.strokeStyle = 'rgba(0,60,120,0.7)'
  ctx.lineWidth = 1.5
  ctx.beginPath(); ctx.arc(0, 0, R * 0.65, 0, Math.PI); ctx.stroke()
  ctx.beginPath(); ctx.arc(0, 0, R * 0.65, Math.PI, Math.PI * 2); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(-R * 0.9, 0); ctx.lineTo(R * 0.9, 0); ctx.stroke()
  ctx.restore()
}
